import { useTranslation } from 'react-i18next';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { RadioCardGroup, RadioCard } from '../../../components/ui/radio-card';
import { JOB_FUNCTIONS, JOB_LEVELS, type JobFunction, type JobLevel } from './options';

/** The personal-profiling fields shared by the create-flow ProfileStep (Flow A)
 *  and the joiner-flow JoinProfileStep (Flow B): job function (+ free-text when
 *  "other") and job level. */
export interface JobRoleValue {
  jobFunction: JobFunction | '';
  jobFunctionCustom: string;
  jobLevel: JobLevel | '';
}

interface JobRoleFieldsProps {
  value: JobRoleValue;
  onChange: (patch: Partial<JobRoleValue>) => void;
}

export function JobRoleFields({ value, onChange }: JobRoleFieldsProps) {
  const { t } = useTranslation(['user_management', 'common']);

  return (
    <div className="space-y-6">
      {/* Job function */}
      <div className="space-y-2">
        <Label>{t('fields.jobFunction')}</Label>
        <RadioCardGroup
          value={value.jobFunction}
          onValueChange={(v) => onChange({ jobFunction: v as JobFunction })}
          className="grid grid-cols-1 gap-2 sm:grid-cols-2"
        >
          {JOB_FUNCTIONS.map((fn) => (
            <RadioCard key={fn.value} value={fn.value} label={t(fn.labelKey)} className="p-3" />
          ))}
        </RadioCardGroup>
        {value.jobFunction === 'other' && (
          <div className="animate-in fade-in slide-in-from-top-2 space-y-1.5 pt-1">
            <Label htmlFor="v2-job-function-custom">{t('fields.jobFunctionCustom')}</Label>
            <Input
              id="v2-job-function-custom"
              value={value.jobFunctionCustom}
              onChange={(e) => onChange({ jobFunctionCustom: e.target.value })}
            />
          </div>
        )}
      </div>

      {/* Job level */}
      <div className="space-y-2">
        <Label>{t('fields.jobLevel')}</Label>
        <RadioCardGroup
          value={value.jobLevel}
          onValueChange={(v) => onChange({ jobLevel: v as JobLevel })}
          className="grid grid-cols-1 gap-2 sm:grid-cols-2"
        >
          {JOB_LEVELS.map((level) => (
            <RadioCard key={level.value} value={level.value} label={t(level.labelKey)} className="p-3" />
          ))}
        </RadioCardGroup>
      </div>
    </div>
  );
}
